import { DateTime } from 'luxon';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { wrapRequest } from 'helpers/requests';

import { media } from 'context';
import {
  APP_BANNER_HEIGHT,
  Avatar,
  BOTTOM_NAVIGATION_HEIGHT,
  IconButton,
  Layout,
  PageLayout,
  PillButton,
  RequestState,
  RichTextDisplay,
  StickyContainer,
  Text
} from 'ds';
import SupportTicketMessages from 'ds/SupportTicketMessages';
import { namePartsToFirstLastInitial } from 'ds/utils';
import { CUSTOMER_SUPPORT_PATH } from 'routes';
import { actions } from 'store/AIAssistant';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUIState } from 'store/UI/selectors';

import { convertTenantEmployeeRequestToSupportTicket, rejectTenantEmployeeRequest } from './requests';
import { TenantEmployeeRequest } from './types';

const OPEN_HEADING = 'Move forward with this employee request?';
const OPEN_BODY = 'Mark a request Open to send it to the Codi team. Mark it Closed to archive it.';
const CLOSED_HEADING = 'Closed';
const CLOSED_BODY = 'Mark a request Open to send it to the Codi team.';

interface Props {
  tenantEmployeeRequest: TenantEmployeeRequest;
  onRequestUpdated?: (request: TenantEmployeeRequest) => void;
}

const TenantEmployeeRequestPageContent: React.FC<Props> = ({
  tenantEmployeeRequest: {
    id,
    title,
    uuid,
    reporter,
    description,
    created_at,
    customer,
    ai_assistant_conversation_id,
    messages,
    rejected_at
  },
  onRequestUpdated
}) => {
  const isClosed = rejected_at !== null && rejected_at !== undefined;
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isMobile } = useContext(media);
  const [requestState, setRequestState] = useState<RequestState>('ready');
  const [showAbsoluteTime, setShowAbsoluteTime] = useState<boolean>(true);
  const tenancyId = useAppSelector(state => state.v2.User.user?.tenancy_id);
  const { appBannerType } = useAppSelector(selectUIState);

  const messageDate = DateTime.fromISO(created_at);
  const timeDisplay = messageDate.toFormat('LLL d, h:mm a');

  const noOpEmojiSelect = () => {};

  const handleMarkAsOpen = async () => {
    if (!tenancyId || requestState === 'in_progress') return;

    setRequestState('in_progress');
    try {
      const { data } = await wrapRequest(convertTenantEmployeeRequestToSupportTicket({ tenancyId, requestId: id }));
      dispatch(actions.setSupportTickets([data]));
      history.push(`${CUSTOMER_SUPPORT_PATH}/${data.id}`);
    } catch {
      // Error shown by wrapRequest
    } finally {
      setRequestState('ready');
    }
  };

  const handleMarkAsClosed = async () => {
    if (!tenancyId || requestState === 'in_progress') return;

    setRequestState('in_progress');
    try {
      const { data } = await wrapRequest(rejectTenantEmployeeRequest({ tenancyId, requestId: id }), {
        successMessage: 'Request marked as closed.',
        errorMessage: 'Failed to mark request as closed'
      });
      onRequestUpdated?.(data);
      history.push(`${CUSTOMER_SUPPORT_PATH}?status=closed`);
    } catch {
      // Error shown by wrapRequest
    } finally {
      setRequestState('ready');
    }
  };

  if (isMobile) {
    return (
      <Layout flex direction="column" width="100%" height="100%">
        <StickyContainer zIndex={1000} direction="column" top={appBannerType ? APP_BANNER_HEIGHT : 0}>
          <Layout
            width="100%"
            height={60}
            background="white"
            paddingTop={12}
            gap={8}
            flex={true}
            align="center"
            direction="row"
            paddingX={8}
          >
            <IconButton
              size="lg"
              iconSize={28}
              type="noBackground"
              name="leftChevron"
              href={`${CUSTOMER_SUPPORT_PATH}?status=tenant_employee`}
            />
            <Layout direction="row" gap={12} flexGrow={1}>
              <Layout minWidth={36} minHeight={36}>
                <Avatar size={36} avatarUrl={reporter?.avatar_url} />
              </Layout>
              <Layout direction="column">
                <Layout justify="space-between" align="center">
                  <Text size="body-md" semibold>
                    {title}
                  </Text>
                </Layout>
                <Layout align="center" gap={4}>
                  <Text size="body-xs" color="gray-600">
                    #{uuid.split('-')[0].toUpperCase()},{' '}
                    {namePartsToFirstLastInitial({
                      firstname: reporter?.first,
                      lastname: reporter?.last
                    })}
                  </Text>
                  {ai_assistant_conversation_id && (
                    <Text size="body-xs" color="gray-600" italic>
                      via Codi AI
                    </Text>
                  )}
                </Layout>
              </Layout>
            </Layout>
          </Layout>
        </StickyContainer>
        <Layout direction="column" flexGrow={1} minHeight={0} overflowY="auto" paddingBottom={220}>
          <Layout paddingX={16} marginBottom={24}>
            <Layout flexGrow={1} color="white">
              <Layout marginTop={24} direction="row">
                <Layout minWidth={32} minHeight={32}>
                  <Avatar size={32} avatarUrl={reporter?.avatar_url} borderWidth={0} defaultIcon="avatar" />
                </Layout>
                <Layout marginLeft={16} />
                <Layout direction="column" flexGrow={1}>
                  <Layout justify="space-between" align="center">
                    <Layout paddingY={6} gap={8} flex>
                      <Text size="body-xs" ellipsis>
                        {namePartsToFirstLastInitial({
                          firstname: reporter?.first,
                          lastname: reporter?.last
                        })}
                        {customer && ` • ${customer.name}`}
                      </Text>
                      <Layout flexShrink={0} align="center">
                        <Text size="body-xs" color="gray-400">
                          {timeDisplay}
                        </Text>
                      </Layout>
                    </Layout>
                  </Layout>
                  <Layout marginTop={4} />
                  <Layout direction="column">
                    <Layout align="flex-start">
                      <Layout maxWidth="calc(100vw - 88px)">
                        <Text size="body-sm">
                          <RichTextDisplay value={description || ''} />
                        </Text>
                      </Layout>
                    </Layout>
                  </Layout>
                </Layout>
              </Layout>
            </Layout>
          </Layout>
          <Layout paddingX={16}>
            <SupportTicketMessages
              supportTicket={{ id, messages, title, ai_assistant_conversation_id }}
              onEmojiSelect={noOpEmojiSelect}
              readOnly
              showAbsoluteTime={showAbsoluteTime}
              onToggleTimeFormat={() => setShowAbsoluteTime(prev => !prev)}
            />
          </Layout>
        </Layout>
        {isClosed ? (
          <Layout
            position="fixed"
            bottom={BOTTOM_NAVIGATION_HEIGHT}
            left={0}
            right={0}
            color="gold-100"
            direction="column"
            gap={12}
            padding={16}
            zIndex={100}
          >
            <Text size="body-sm" semibold>
              {CLOSED_HEADING}
            </Text>
            <Text size="body-xs" color="gray-700">
              {CLOSED_BODY}
            </Text>
            <PillButton
              type="purpleGradient"
              size="md"
              text="Mark as open"
              onClick={handleMarkAsOpen}
              disabled={requestState === 'in_progress'}
              fullWidth
            />
          </Layout>
        ) : (
          <Layout
            position="fixed"
            bottom={BOTTOM_NAVIGATION_HEIGHT}
            left={0}
            right={0}
            color="gold-100"
            direction="column"
            gap={12}
            padding={16}
            zIndex={100}
          >
            <Text size="body-sm" semibold>
              {OPEN_HEADING}
            </Text>
            <Text size="body-xs" color="gray-700">
              {OPEN_BODY}
            </Text>
            <Layout direction="column" gap={8}>
              <PillButton
                type="purpleGradient"
                size="md"
                text="Mark as open"
                onClick={handleMarkAsOpen}
                disabled={requestState === 'in_progress'}
                fullWidth
              />
              <PillButton
                type="secondaryOutline"
                size="md"
                text="Mark as closed"
                onClick={handleMarkAsClosed}
                disabled={requestState === 'in_progress'}
                fullWidth
              />
            </Layout>
          </Layout>
        )}
      </Layout>
    );
  }

  return (
    <PageLayout
      type="default"
      maxWidth={1680}
      color="blue-100"
      title=""
      titleColor="gray-900"
      backLink={{ href: CUSTOMER_SUPPORT_PATH, search: '?status=tenant_employee' }}
      navBarTopOffset={appBannerType ? APP_BANNER_HEIGHT : 0}
    >
      <Layout justify="center">
        <Layout maxWidth={724} flexGrow={1}>
          <Layout
            color="white"
            padding={24}
            borderTopLeftRadius={16}
            borderTopRightRadius={16}
            direction="column"
            gap={24}
          >
            <Layout direction="row" justify="space-between" align="center">
              <Layout direction="column" gap={4}>
                <Text size="headline-md">{title}</Text>
                <Layout align="center" gap={4}>
                  <Text size="body-xs" color="gray-600">
                    #{uuid.split('-')[0].toUpperCase()},{' '}
                    {namePartsToFirstLastInitial({
                      firstname: reporter?.first,
                      lastname: reporter?.last
                    })}
                  </Text>
                  {ai_assistant_conversation_id && (
                    <Text size="body-xs" color="gray-600" italic>
                      via Codi AI
                    </Text>
                  )}
                </Layout>
              </Layout>
            </Layout>

            <Layout direction="row">
              <Layout minWidth={32} minHeight={32}>
                <Avatar size={32} avatarUrl={reporter?.avatar_url} borderWidth={0} defaultIcon="avatar" />
              </Layout>
              <Layout marginLeft={16} />
              <Layout direction="column" flexGrow={1}>
                <Layout justify="space-between" align="center">
                  <Layout paddingY={6} gap={8} flex>
                    <Text size="body-xs" ellipsis>
                      {namePartsToFirstLastInitial({
                        firstname: reporter?.first,
                        lastname: reporter?.last
                      })}
                      {customer && ` • ${customer.name}`}
                    </Text>
                    <Layout flexShrink={0} align="center">
                      <Text size="body-xs" color="gray-400">
                        {timeDisplay}
                      </Text>
                    </Layout>
                  </Layout>
                </Layout>
                <Layout marginTop={4} />
                <Layout direction="column">
                  <Layout align="flex-start">
                    <Layout maxWidth={556}>
                      <Text size="body-sm">
                        <RichTextDisplay value={description || ''} />
                      </Text>
                    </Layout>
                  </Layout>
                </Layout>
              </Layout>
            </Layout>
            {messages.length > 0 && (
              <Layout marginTop={8}>
                <SupportTicketMessages
                  supportTicket={{ id, messages, title, ai_assistant_conversation_id }}
                  onEmojiSelect={noOpEmojiSelect}
                  readOnly
                  showAbsoluteTime={showAbsoluteTime}
                  onToggleTimeFormat={() => setShowAbsoluteTime(prev => !prev)}
                />
              </Layout>
            )}
          </Layout>
          {isClosed ? (
            <Layout
              padding={24}
              color="gold-100"
              direction="column"
              gap={16}
              borderBottomLeftRadius={16}
              borderBottomRightRadius={16}
            >
              <Layout direction="column" gap={8}>
                <Text size="body-sm" semibold>
                  {CLOSED_HEADING}
                </Text>
                <Text size="body-xs" color="gray-700">
                  {CLOSED_BODY}
                </Text>
              </Layout>
              <Layout width={144} minWidth={144}>
                <PillButton
                  type="purpleGradient"
                  size="md"
                  text="Mark as open"
                  onClick={handleMarkAsOpen}
                  disabled={requestState === 'in_progress'}
                  fullWidth
                />
              </Layout>
            </Layout>
          ) : (
            <Layout
              padding={24}
              color="gold-100"
              direction="column"
              gap={16}
              borderBottomLeftRadius={16}
              borderBottomRightRadius={16}
            >
              <Layout direction="column" gap={8}>
                <Text size="body-sm" semibold>
                  {OPEN_HEADING}
                </Text>
                <Text size="body-xs" color="gray-700">
                  {OPEN_BODY}
                </Text>
              </Layout>
              <Layout direction="row" gap={12}>
                <Layout width={144} minWidth={144}>
                  <PillButton
                    type="purpleGradient"
                    size="md"
                    text="Mark as open"
                    onClick={handleMarkAsOpen}
                    disabled={requestState === 'in_progress'}
                    fullWidth
                  />
                </Layout>
                <Layout width={144} minWidth={144}>
                  <PillButton
                    type="secondaryOutline"
                    size="md"
                    text="Mark as closed"
                    onClick={handleMarkAsClosed}
                    disabled={requestState === 'in_progress'}
                    fullWidth
                  />
                </Layout>
              </Layout>
            </Layout>
          )}
        </Layout>
      </Layout>
    </PageLayout>
  );
};

export default TenantEmployeeRequestPageContent;
